import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple } from '@phosphor-icons/react';

export default function ScoringRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', field: 'industry_normalized', operator: 'contains', value: '', score: 10, category: 'industry' });

  const fetchRules = async () => {
    try { const { data } = await api.get('/scoring/rules'); setRules(data); } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetchRules(); }, []);

  const addRule = async () => {
    try {
      await api.post('/scoring/rules', form);
      toast.success('Rule created');
      setShowAdd(false);
      setForm({ name: '', field: 'industry_normalized', operator: 'contains', value: '', score: 10, category: 'industry' });
      fetchRules();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteRule = async (id) => {
    try { await api.delete(`/scoring/rules/${id}`); toast.success('Rule deleted'); fetchRules(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const recalcAll = async () => {
    try { const { data } = await api.post('/scoring/calculate-all'); toast.success(data.message); } catch (err) { toast.error(formatApiError(err)); }
  };

  const categoryColor = (cat) => ({
    industry: 'bg-blue-50 text-blue-700 border-blue-200',
    website: 'bg-purple-50 text-purple-700 border-purple-200',
    location: 'bg-green-50 text-green-700 border-green-200',
    company_size: 'bg-amber-50 text-amber-700 border-amber-200',
    custom: 'bg-gray-100 text-gray-700 border-gray-200',
  }[cat] || 'bg-gray-100 text-gray-600 border-gray-200');

  return (
    <div data-testid="scoring-rules-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">Scoring Rules</h1>
          <p className="text-sm text-gray-500 mt-1">Configure how companies are scored. Rules evaluate fields like industry, location, and website presence. After changing rules, use "Recalculate All" to update all company scores and campaign readiness.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={recalcAll} data-testid="recalc-all-btn">Recalculate All</Button>
          <Button onClick={() => setShowAdd(true)} className="bg-black text-white hover:bg-gray-800" data-testid="add-rule-btn">
            <Plus size={16} className="mr-2" /> Add Rule
          </Button>
        </div>
      </div>

      <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b border-gray-200">
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Rule</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Field</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Operator</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Value</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Score</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Category</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Active</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="8" className="py-12 text-center text-gray-400">Loading...</td></tr> :
            rules.length === 0 ? <tr><td colSpan="8" className="py-12 text-center text-gray-400">No scoring rules configured</td></tr> :
            rules.map(r => (
              <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm font-medium text-gray-900">{r.name}</td>
                <td className="py-3 px-4 text-xs font-mono text-gray-600">{r.field}</td>
                <td className="py-3 px-4 text-xs font-mono text-gray-600">{r.operator}</td>
                <td className="py-3 px-4 text-xs font-mono text-gray-600">{r.value || '-'}</td>
                <td className="py-3 px-4">
                  <span className={`font-mono text-sm font-bold ${r.score >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {r.score > 0 ? '+' : ''}{r.score}
                  </span>
                </td>
                <td className="py-3 px-4"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${categoryColor(r.category)}`}>{r.category}</span></td>
                <td className="py-3 px-4"><span className={`text-xs font-mono ${r.is_active ? 'text-emerald-600' : 'text-gray-400'}`}>{r.is_active ? 'yes' : 'no'}</span></td>
                <td className="py-3 px-4"><button onClick={() => deleteRule(r.id)} className="text-gray-400 hover:text-red-600"><Trash size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add Scoring Rule</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Name</label>
              <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="AV Company Bonus" data-testid="rule-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Field</label>
                <Input value={form.field} onChange={e => setForm(p => ({...p, field: e.target.value}))} placeholder="industry_normalized" /></div>
              <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Operator</label>
                <Input value={form.operator} onChange={e => setForm(p => ({...p, operator: e.target.value}))} placeholder="contains" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Value</label>
                <Input value={form.value} onChange={e => setForm(p => ({...p, value: e.target.value}))} placeholder="av" /></div>
              <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Score</label>
                <Input type="number" value={form.score} onChange={e => setForm(p => ({...p, score: parseInt(e.target.value) || 0}))} /></div>
            </div>
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Category</label>
              <Input value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))} placeholder="industry" /></div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={addRule} className="bg-black text-white hover:bg-gray-800" data-testid="save-rule-btn" disabled={!form.name}>Save Rule</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
